import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import '../styles/LandingPage.css';

// Import Kenyan coat of arms logo
import kenyaArmsLogo from '../assets/kenya_coat_of_arms.png';

const LandingPage: React.FC = () => {
  return (
    <Box className="landing-page">
      {/* Hero Section with Background Image */}
      <Box className="hero-section">
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <img 
                src={kenyaArmsLogo} 
                alt="Kenya Coat of Arms" 
                className="kenya-arms-logo" 
              />
              <Typography variant="h5" component="div" sx={{ ml: 2, fontWeight: 'bold' }}>
                Ministry of Health
              </Typography>
            </Box>
            <Button 
              component={Link} 
              to={`${localStorage.getItem('cqi_user') ? '/dashboard' : '/login'}`} 
              variant="contained" 
              color="primary"
              endIcon={<ArrowForwardIcon />}
            >
              {localStorage.getItem('cqi_user') ? 'Access Dashboard' : 'Login'}
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" className="hero-content" sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h2" component="h1" className="hero-title">
                Continuous Quality Improvement
              </Typography>
              <Typography variant="h5" component="h2" className="hero-subtitle">
                Enhancing Healthcare Services in Kenya
              </Typography>
              <Typography variant="body1" className="hero-description" paragraph sx={{ maxWidth: '800px', mx: 'auto' }}>
                A systematic approach to improving service delivery processes and outcomes
                for Kenya's community health workers and healthcare facilities.
              </Typography>
              <Button 
                component={Link} 
                to={`${localStorage.getItem('cqi_user') ? '/dashboard' : '/login'}`} 
                variant="contained" 
                size="large"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                sx={{ mt: 2 }}
              >
                {localStorage.getItem('cqi_user') ? 'Access Dashboard': 'Login'}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What is CQI Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom>
            What is Continuous Quality Improvement?
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto' }}>
            Continuous Quality Improvement (CQI) is a structured operational process that engages 
            staff to identify and improve the processes and outcomes of care on an ongoing basis.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardMedia
                component="div"
                sx={{
                  height: 180,
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box className="card-icon" />
              </CardMedia>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Data-Driven Decisions
                </Typography>
                <Typography variant="body2">
                  CQI relies on accurate data collection, analysis, and interpretation to identify problems
                  and monitor the effectiveness of interventions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardMedia
                component="div"
                sx={{
                  height: 180,
                  backgroundColor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box className="card-icon" />
              </CardMedia>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Continuous Monitoring
                </Typography>
                <Typography variant="body2">
                  Regular evaluation of key performance indicators ensures that improvements are sustained
                  and healthcare quality continuously advances.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%' }}>
              <CardMedia
                component="div"
                sx={{
                  height: 180,
                  backgroundColor: 'info.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box className="card-icon" />
              </CardMedia>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Collaborative Approach
                </Typography>
                <Typography variant="body2">
                  CQI involves all stakeholders, from community health workers to facility managers,
                  working together to identify issues and implement solutions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom textAlign="center" sx={{ mb: 5 }}>
            Benefits of CQI in Healthcare
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" component="h3" gutterBottom>
                  For Healthcare Workers
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Improved work environment and job satisfaction" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Enhanced skills and professional development" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Evidence-based practice implementation" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Better communication and teamwork" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" component="h3" gutterBottom>
                  For Patients and Communities
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Better health outcomes and quality of care" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Improved access to healthcare services" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Enhanced patient satisfaction and experience" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Reduction in healthcare disparities" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom>
            Join the Quality Improvement Journey
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Be part of Kenya's healthcare transformation. Access the CQI Dashboard to start
            tracking, managing, and improving healthcare quality indicators.
          </Typography>
          <Button 
            component={Link} 
            to={`${localStorage.getItem('cqi_user') ? '/dashboard' : '/login'}`}  
            variant="contained" 
            size="large"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            sx={{ px: 4, py: 1 }}
          >
            {localStorage.getItem('cqi_user') ? 'Access Dashboard' : 'Login to Dashboard'}
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src={kenyaArmsLogo} 
                  alt="Kenya Coat of Arms" 
                  className="kenya-arms-logo-small" 
                />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Ministry of Health, Kenya
                </Typography>
              </Box>
              <Typography variant="body2">
                Committed to providing the highest quality healthcare services to all citizens.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ mt: 2 }}>
                © {new Date().getFullYear()} CQI Dashboard - Ministry of Health, Kenya. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
